import { api } from "../../lib/api";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Car, Users, ChartLineUp, TrendUp, MapPin, Clock, Receipt, Ticket, Wallet, CalendarBlank, UserPlus, ChatCircleDots, Target, Timer } from "@phosphor-icons/react";
import { StatTile, Panel, StatusPill, EmptyState, money, count as fmt, dt } from "../lux";
import { PeriodPicker, periodRange, humanMinutes } from "../PeriodPicker";
import { InfoHint, RECOGNITION_HINTS } from "../InfoHint";
import { TeamPerformancePanel, TopVehiclesPanel } from "./PerformancePanels";

// DASH-002: the "Numbers" tab — the period-driven figures DASH-001 built,
// unchanged. Live-ops tiles and the schedule/attention panels now live on Today.
export default function NumbersView() {
  const [period, setPeriod] = useState("month");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const range = periodRange(period, custom);

  const { data: summary } = useQuery({
    queryKey: ["dashboard-summary"],
    queryFn: async () => (await api.get("/dashboard/summary")).data,
  });
  const { data: p } = useQuery({
    queryKey: ["dashboard-period", range.from, range.to],
    queryFn: async () => (await api.get("/dashboard/period", {
      params: { from: range.from || undefined, to: range.to || undefined },
    })).data,
  });

  if (!summary) return <div className="text-muted-foreground label-mono">Loading numbers…</div>;

  const finOk = summary.financials_visible && p?.financials_visible !== false;
  const m = p?.money || {};
  const l = p?.leads || {};
  const fleet = p?.fleet || {};
  const label = range.label;
  const utilization = fleet.utilization ?? summary.fleet_utilization ?? 0;

  return (
    <div className="space-y-8" data-testid="numbers-view">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PeriodPicker value={period} onChange={setPeriod} custom={custom} onCustom={setCustom} testid="numbers-period" />
        <span className="label-mono">{range.from || "start"} → {range.to || "today"}</span>
      </div>

      {finOk && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 stagger" data-testid="dashboard-money-row">
          <StatTile
            label={<span>Earned Revenue <InfoHint text={RECOGNITION_HINTS.earned} /></span>}
            value={money(m.earned_revenue)} gold delta={m.earned_delta}
            deltaLabel={`vs ${money(m.earned_prev)} prior period`}
            hint={`${fmt(m.bookings_count)} bookings ${label}`}
            icon={ChartLineUp} tone="gold" testid="kpi-earned-revenue" to="/financials"
          />
          <StatTile
            label={<span>Booked Revenue <InfoHint text={RECOGNITION_HINTS.booked} /></span>}
            value={money(m.booked_revenue)} delta={m.booked_delta}
            deltaLabel={`vs ${money(m.booked_prev)} prior period`}
            hint={`Sold ${label}`} icon={TrendUp} tone="blue" testid="kpi-booked-revenue"
          />
          <StatTile label="Net Profit" value={money(m.net_profit)} hint={`After costs, cuts & payouts`} icon={Wallet} tone="green" testid="kpi-net-profit" to="/financials" />
          <StatTile
            label={<span>Collected Cash <InfoHint text={RECOGNITION_HINTS.collected} /></span>}
            value={money(m.collected_cash)} hint="Deposits + invoice payments" icon={Receipt} tone="neutral" testid="kpi-collected-cash"
          />
          <StatTile label="Outstanding Balance" value={money(m.outstanding_balance)} hint="Owed on cars out + unpaid invoices" icon={Ticket} tone="amber" testid="kpi-outstanding" to="/invoices" />
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 stagger" data-testid="dashboard-leads-row">
        <StatTile label="New Leads" value={fmt(l.new_leads)} hint={`Came in ${label}`} icon={UserPlus} tone="blue" testid="kpi-new-leads" to="/leads" />
        <StatTile label="New Leads Contacted" value={fmt(l.contacted)} hint={`${l.contacted_pct || 0}% of this period's new leads`} icon={ChatCircleDots} tone="green" testid="kpi-leads-contacted" />
        <StatTile label="Conversion Rate" value={`${l.conversion_rate || 0}%`} hint={`${fmt(l.won)} won of ${fmt(l.new_leads)} new`} icon={Target} tone="gold" testid="kpi-conversion" />
        <StatTile label="Median Response Time" value={humanMinutes(l.median_response_minutes)} hint="First reply after a lead lands" icon={Timer} tone="neutral" testid="kpi-response-time" />
        <StatTile
          label="Leads Awaiting Reply" value={fmt(l.awaiting_reply)}
          hint={l.awaiting_over_24h ? `${fmt(l.awaiting_over_24h)} waiting over 24h` : "All fresh"}
          icon={Users} tone={l.awaiting_over_24h ? "amber" : "neutral"} testid="kpi-awaiting-reply" to="/leads"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <TeamPerformancePanel rows={p?.team} moneyOk={finOk} scope={p?.scope} me={p?.me} label={label} />
        </div>
        {finOk && <TopVehiclesPanel rows={p?.top_vehicles} moneyOk={finOk} label={label} />}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel label="Fleet" title="At a glance" icon={Car}>
          <Row label="Total vehicles" value={fmt(fleet.total)} />
          <Row label="Available" value={fmt(fleet.available)} accent />
          <Row label="In use" value={fmt(fleet.in_use)} />
          <Row label="Maintenance" value={fmt(fleet.maintenance)} muted />
          {fleet.retired > 0 && <Row label="Retired (excluded)" value={fmt(fleet.retired)} muted />}
          <div className="mt-5">
            <div className="flex items-center justify-between mb-2">
              <span className="label-mono">Capacity in use</span>
              <span className="text-xs tnum text-muted-foreground">{utilization}%</span>
            </div>
            <div className="h-1.5 w-full bg-white/[0.06] rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${Math.min(100, utilization)}%` }} />
            </div>
          </div>
        </Panel>

        <Panel label="Pipeline" title="Next 7 days" icon={CalendarBlank} className="lg:col-span-2">
          <div className="font-heading text-5xl tnum">{summary.bookings_this_week?.length || 0}</div>
          <div className="text-xs text-muted-foreground mt-1.5">scheduled engagements</div>
          <div className="mt-5 pt-4 border-t border-border/60">
            <Row label="Open leads" value={fmt(summary.open_leads)} icon={TrendUp} />
            <Row label="Clients" value={fmt(summary.total_customers)} icon={Users} />
            <Row label="Active drivers" value={fmt(summary.active_chauffeurs)} accent />
          </div>
        </Panel>
      </div>

      <Panel label="On the road" title={`Cars currently out (${summary.out_bookings?.length || 0})`} icon={Car} flush>
        {(summary.out_bookings?.length || 0) === 0 ? (
          <EmptyState icon={Car} title="No cars are out right now" hint="Active rentals show up here the moment they're marked out." />
        ) : (
          <div className="divide-y divide-border/60" data-testid="out-bookings">
            <div className="px-5 py-2.5 grid grid-cols-12 gap-4 items-center label-mono">
              <div className="col-span-5">Client</div>
              <div className="col-span-4">Vehicle</div>
              <div className="col-span-3 text-right">Return date</div>
            </div>
            {summary.out_bookings.map((b) => (
              <Link to={`/bookings/view/${b.id}`} key={b.id} className="px-5 py-3.5 grid grid-cols-12 gap-4 items-center hover:bg-white/[0.03] transition-colors" data-testid={`out-booking-${b.id}`}>
                <div className="col-span-5 font-heading text-base truncate">{b.customer_name || "Guest"}</div>
                <div className="col-span-4 text-sm truncate text-muted-foreground">{b.vehicle_label || b.tour_name || "—"}</div>
                <div className="col-span-3 text-right text-sm tnum">{dt(b.end_datetime, { month: "short", day: "numeric", year: "numeric" })}</div>
              </Link>
            ))}
          </div>
        )}
      </Panel>

      <Panel label="Next departures" title="Engagements this week" icon={Clock} flush>
        {(summary.bookings_this_week?.length || 0) === 0 ? (
          <EmptyState icon={Clock} title="Nothing scheduled in the next 7 days" />
        ) : (
          <div className="divide-y divide-border/60" data-testid="upcoming-bookings">
            {summary.bookings_this_week.map((b) => (
              <Link to={`/bookings/view/${b.id}`} key={b.id} className="px-5 py-3.5 grid grid-cols-12 gap-4 items-center hover:bg-white/[0.03] transition-colors">
                <div className="col-span-4 sm:col-span-3 min-w-0">
                  <div className="font-heading text-base truncate">{b.customer_name || "Guest"}</div>
                  <div className="label-mono mt-0.5">{b.booking_type}</div>
                </div>
                <div className="col-span-4 sm:col-span-3 text-sm min-w-0 hidden sm:block">
                  <div className="flex items-center gap-1.5 text-muted-foreground truncate">
                    <MapPin size={12} /> {b.pickup_location || "—"}
                  </div>
                </div>
                <div className="col-span-4 sm:col-span-3 text-sm truncate text-muted-foreground">{b.vehicle_label || b.tour_name || "—"}</div>
                <div className="col-span-2 text-sm tnum hidden sm:block">{dt(b.start_datetime, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
                <div className="col-span-4 sm:col-span-1 flex justify-end"><StatusPill status={b.status} /></div>
              </Link>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

function Row({ label, value, accent, muted, icon: Icon }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-border/40 last:border-b-0">
      <span className="text-sm flex items-center gap-2 text-muted-foreground">
        {Icon && <Icon size={14} />} {label}
      </span>
      <span className={`font-heading text-lg tnum ${accent ? "text-primary" : muted ? "text-muted-foreground" : ""}`}>{value}</span>
    </div>
  );
}
